import { inject } from '@angular/core';
import { Router, Routes } from '@angular/router';
import { AuthService } from './core/services/auth.service';
import { publicGuard } from './core/guards/public.guard';
import { roleGuard } from './core/guards/role.guard';
import { authGuard } from './core/guards/auth.guard';
import { trackingDispatchGuard } from './core/guards/tracking-dispatch.guard';
import {
  alreadyRotatedGuard,
  mustChangePasswordGuard,
} from './core/guards/must-change-password.guard';
import { landingPathFor } from './core/auth/landing';

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: () => {
      const auth = inject(AuthService);
      const router = inject(Router);

      if (!auth.hasAccessToken()) {
        return router.createUrlTree(['/login']);
      }
      return landingPathFor(auth.role());
    },
  },
  {
    path: 'login',
    canMatch: [publicGuard],
    loadComponent: () => import('./features/auth/login.page').then((m) => m.LoginPage),
  },
  {
    // Deliberately no publicGuard — reachable from /login while logged out, but also from
    // /change-password's "Forgot Password" link while fully authenticated (a valid session
    // doesn't mean the user remembers their *current* password). publicGuard would bounce that
    // second case straight back to the dashboard before the form ever rendered.
    path: 'forgot-password',
    loadComponent: () =>
      import('./features/auth/forgot-password.page').then((m) => m.ForgotPasswordPage),
  },
  {
    // Deliberately no publicGuard — an authenticated user must still be able to follow a reset
    // link (requested on one device, opened on another where they're already signed in
    // elsewhere). See docs/adr/0014-*.md.
    path: 'reset-password',
    loadComponent: () =>
      import('./features/auth/reset-password.page').then((m) => m.ResetPasswordPage),
  },
  {
    // The path the printed QR code encodes — see FRONTEND_TRACKING_BASE_URL in cftw-be
    // (`.../tracking/:trackingToken`). trackingDispatchGuard always resolves to a UrlTree
    // (role-appropriate tracking route, /forbidden, or /login with a redirectTo back here) —
    // `redirectTo` can't be paired with `canMatch` on the same route (NG04014: redirects
    // happen before guards run), so this loadComponent target is genuinely dead code, kept
    // only so the route has a valid structural fallback if the guard is ever misconfigured
    // to return `true`.
    path: 'tracking/:token',
    canMatch: [trackingDispatchGuard],
    loadComponent: () => import('./features/errors/not-found.page').then((m) => m.NotFoundPage),
  },
  {
    path: 'tracking',
    pathMatch: 'full',
    redirectTo: '/',
  },
  {
    path: '',
    canMatch: [authGuard],
    loadComponent: () => import('./layout/shell.component').then((m) => m.ShellComponent),
    children: [
      {
        // Outside the mustChangePasswordGuard wrapper below, on purpose — it can't gate its own
        // destination. alreadyRotatedGuard is the inverse check: a user who reaches this route
        // with mustChangePassword already false gets sent to the voluntary /change-password
        // instead of rendering a "forced" screen with nothing left to force. See docs/adr/0014-*.md.
        path: 'set-password',
        canMatch: [alreadyRotatedGuard],
        loadComponent: () =>
          import('./features/auth/set-password.page').then((m) => m.SetPasswordPage),
      },
      {
        // One pathless wrapper carrying the forced-rotation gate for everything else in the
        // shell — designer/admin subtrees plus the voluntary change-password screen. A single
        // canMatch site here (rather than repeating it on 'designer' and 'admin' separately)
        // per docs/adr/0014-*.md.
        path: '',
        canMatch: [mustChangePasswordGuard],
        children: [
          {
            path: 'designer',
            canMatch: [roleGuard(['DESIGNER'])],
            loadChildren: () => import('./features/designer/designer.routes'),
          },
          {
            // roleGuard auto-passes SUPER_ADMIN internally (FE ADR 0008) — ['ADMIN'] alone is
            // correct.
            path: 'admin',
            canMatch: [roleGuard(['ADMIN'])],
            loadChildren: () => import('./features/admin/admin.routes'),
          },
          {
            path: 'judgement',
            canMatch: [roleGuard(['JUDGE'])],
            loadChildren: () => import('./features/judgement/judgement.routes'),
          },
          {
            // Voluntary change-password, reachable by any authenticated role — not role-scoped
            // like designer/admin above, so it lives at this level rather than under either
            // subtree's own routes file.
            path: 'change-password',
            loadComponent: () =>
              import('./features/auth/change-password.page').then((m) => m.ChangePasswordPage),
          },
        ],
      },
    ],
  },
  {
    path: 'forbidden',
    loadComponent: () => import('./features/errors/forbidden.page').then((m) => m.ForbiddenPage),
  },
  {
    path: '**',
    loadComponent: () => import('./features/errors/not-found.page').then((m) => m.NotFoundPage),
  },
];
